import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import FormModal from '@/components/v2/molecules/admin/shared/FormModal';
import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Organization, useCreateOrganization, useUpdateOrganization } from "@/hooks/useOrganizations";

const organizationSchema = z.object({
  name: z.string().min(1, "El nombre es obligatorio"),
  type: z.enum(["persona_fisica", "persona_moral"]),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

interface OrganizationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization?: Organization | null;
}

const OrganizationFormModal = ({ isOpen, onClose, organization }: OrganizationFormModalProps) => {
  const { mutate: createOrganization, isPending: isCreating } = useCreateOrganization();
  const { mutate: updateOrganization, isPending: isUpdating } = useUpdateOrganization();

  const form = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: "",
      type: "persona_fisica",
    },
  });

  useEffect(() => {
    if (organization && isOpen) {
      form.reset({
        name: organization.name,
        type: organization.type as "persona_fisica" | "persona_moral",
      });
    } else if (!isOpen) {
      form.reset({
        name: "",
        type: "persona_fisica",
      });
    }
  }, [organization, isOpen, form]);

  const onSubmit = (data: OrganizationFormData) => {
    if (organization) {
      updateOrganization(
        { id: organization.id, ...data },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    } else {
      createOrganization(
        { name: data.name, type: data.type },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={organization ? "Editar Organización" : "Nueva Organización"}
      description={organization
        ? "Modifica los datos de la organización"
        : "Completa los datos para crear una nueva organización"}
      form={form}
      onSubmit={onSubmit}
      isLoading={isLoading}
      submitLabel={organization ? "Actualizar" : "Crear"}
      maxWidth="max-w-[500px]"
    >
      <div className="grid gap-4 py-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre</FormLabel>
              <FormControl>
                <Input placeholder="Nombre de la organización" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de Organización</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona el tipo" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="persona_fisica">Persona Física</SelectItem>
                  <SelectItem value="persona_moral">Persona Moral</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormModal>
  );
};

export default OrganizationFormModal;
